/**
 * Sidebar v2 row tooltip: the hover card that disambiguates identically
 * titled threads.
 *
 * Ported from pingdotgg/t3code `apps/web/src/components/Sidebar.tsx`
 * (SidebarThreadTooltip, ~lines 316-432) at pin
 * e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md). Adapted: project and environment identity come
 * from forge's runtime surface (environment label + machine kind), the
 * provider/model row uses forge's session data, and the pull-request
 * section reuses forge's SidebarThreadPullRequestsTooltipSection. Upstream's
 * per-instance provider badges and branch-mismatch warning have no forge
 * data source in the listing yet; those rows are documented seams.
 */
import { CircleAlertIcon, GitBranchIcon, TerminalIcon } from "lucide-react";
import { cn } from "~/lib/utils";
import { TooltipPopup } from "~/components/ui/tooltip";
import { SidebarV2ProjectIcon, SidebarV2ProviderIcon } from "./SidebarV2IdentityMeta";
import { SidebarV2OriginIcon } from "./SidebarV2IdentityMeta";

/** Upstream carries a per-environment machine-kind glyph enum; forge's
 * listing has no machine-kind source yet, so the prop stays a nullable
 * label-shaped string until that lands. */
type EnvironmentMachineKind = string | null;

import { SidebarThreadPullRequestsTooltipSection } from "~/components/sidebar/SidebarThreadPullRequestBadge";
import type { ThreadPullRequestEntry } from "~/components/ThreadPullRequestIndicators";
import type { TerminalStatusIndicator } from "~/components/sidebar/SidebarThreadRow.helpers";

export interface SidebarV2TooltipProps {
  readonly thread: {
    readonly id: string;
    readonly environmentId: string;
    readonly title: string;
    readonly branch: string | null;
    readonly session: { readonly lastError?: string | null } | null;
    readonly modelSelection?:
      | { readonly provider?: string | null; readonly providerAccountId?: string | null }
      | null
      | undefined;
  };
  readonly projectDisplayName: string | null;
  readonly projectCwd: string | null;
  readonly projectCanonicalIdentity:
    | import("~/components/ProjectIcon").ProjectCanonicalIdentity
    | undefined;
  readonly displayBranch?: string | null;
  readonly threadOrigin?: import("~/components/sidebar/threadOrigin").ThreadOrigin;
  readonly environmentLabel: string | null;
  readonly environmentMachine: EnvironmentMachineKind | null;
  readonly modelLabel: string | null;
  readonly terminalStatus: TerminalStatusIndicator | null;
  readonly terminalProcessCount: number;
  readonly pullRequestEntries: ReadonlyArray<ThreadPullRequestEntry>;
}

/** Format the running terminal-process count with singular or plural wording. */
function terminalProcessLabel(count: number): string {
  return `${count} terminal ${count === 1 ? "process" : "processes"} running`;
}

/** Show thread context, terminal activity, errors, and pull requests in the row tooltip. */
export function SidebarV2Tooltip(props: SidebarV2TooltipProps) {
  const { thread } = props;
  return (
    <TooltipPopup side="right" align="start" sideOffset={4} variant="glass">
      {/* The viewport's own inset (py-1 px-2) plus this one make the floating inset. */}
      <div className="flex min-w-0 max-w-80 flex-col gap-2 px-1 py-2">
        <div className="min-w-0 truncate text-xs leading-tight font-medium text-foreground">
          {thread.title}
        </div>
        <div className="grid gap-1.5 pl-0.5 text-xs text-muted-foreground">
          {props.projectDisplayName ? (
            <div className="flex min-w-0 items-center gap-2">
              <SidebarV2ProjectIcon
                environmentId={thread.environmentId}
                cwd={props.projectCwd}
                name={props.projectDisplayName}
                canonicalIdentity={props.projectCanonicalIdentity}
                className="size-3 shrink-0"
              />
              <div className="min-w-0 truncate text-foreground/75">{props.projectDisplayName}</div>
            </div>
          ) : null}
          {props.environmentLabel ? (
            <div className="flex min-w-0 items-center gap-2">
              <SidebarV2OriginIcon
                origin={props.threadOrigin ?? "current-device"}
                className="size-3"
              />
              <div className="min-w-0 truncate text-foreground/75">{props.environmentLabel}</div>
            </div>
          ) : null}
          {(props.displayBranch ?? thread.branch) ? (
            <div className="flex min-w-0 items-center gap-2 text-foreground/75">
              <GitBranchIcon className="size-3 shrink-0 stroke-muted-foreground" />
              <div className="min-w-0 truncate">{props.displayBranch ?? thread.branch}</div>
            </div>
          ) : null}
          {props.modelLabel ? (
            <div className="flex min-w-0 items-center gap-2">
              <SidebarV2ProviderIcon
                provider={thread.modelSelection?.provider}
                className="size-3"
              />
              <div className="min-w-0 truncate text-foreground/75">
                {props.modelLabel}
                {thread.modelSelection?.providerAccountId
                  ? ` · ${thread.modelSelection.providerAccountId}`
                  : ""}
              </div>
            </div>
          ) : null}
          {props.terminalStatus ? (
            <div className="flex min-w-0 items-center gap-2">
              <TerminalIcon
                aria-hidden
                className={cn("size-3 shrink-0", props.terminalStatus.colorClass)}
              />
              <div className="min-w-0 truncate text-foreground/75">
                {terminalProcessLabel(props.terminalProcessCount)}
              </div>
            </div>
          ) : null}
          {thread.session?.lastError ? (
            <div className="flex min-w-0 items-center gap-2 text-destructive-foreground">
              <CircleAlertIcon className="size-3 shrink-0 stroke-current" />
              <div className="min-w-0 truncate">Error occurred</div>
            </div>
          ) : null}
        </div>
        {props.pullRequestEntries.length > 0 ? (
          <SidebarThreadPullRequestsTooltipSection entries={props.pullRequestEntries} />
        ) : null}
      </div>
    </TooltipPopup>
  );
}
